import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  spinnerCenterRecipe,
  spinnerRecipe,
  type SpinnerRecipeProps,
} from '../recipes/feedback.recipe';
import { Text } from './Text';

export interface SpinnerProps
  extends Omit<React.SVGAttributes<SVGSVGElement>, 'color'>,
    SpinnerRecipeProps {
  /**
   * What a screen reader should announce. Defaults to "Loading".
   *
   * Pass something specific when the page has more than one thing loading —
   * "Loading impounds" is far more use than three identical "Loading"s.
   *
   * `null` means the parent announces instead — Loading does this when it
   * shows a visible title, so the same words are not read out twice.
   */
  label?: string | null;
}

/**
 * Busy indicator.
 *
 * Announced politely via role="status", so a screen reader mentions it when it
 * appears without interrupting whatever the user is doing.
 *
 * For a button that is working, use `<Button loading>` rather than putting a
 * Spinner inside it — that handles the disabled and aria-busy state too.
 */
export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  function Spinner(
    { className, size, tone, label = 'Loading', ...props },
    ref
  ) {
    return (
      <>
        <Loader2
          ref={ref}
          className={cn(spinnerRecipe({ size, tone }), className)}
          aria-hidden="true"
          focusable="false"
          {...props}
        />
        {label === null ? null : (
          <span role="status" className="sr-only">
            {label}
          </span>
        )}
      </>
    );
  }
);

export interface LoadingProps extends SpinnerProps {
  /** Vertical breathing room around the spinner. */
  pad?: 'none' | 'sm' | 'md' | 'lg';
  /** Fill the parent, or the whole viewport. */
  fill?: 'none' | 'parent' | 'screen';
  /**
   * Words under the spinner, for when the wait is long enough to explain —
   * "Validating authentication", "Processing your import". Sixteen screens
   * were building this by hand as a centred div with a spinner and a Text.
   */
  title?: React.ReactNode;
  /** A quieter second line: what is happening, or what to do meanwhile. */
  description?: React.ReactNode;
}

/**
 * A spinner centred in the space available — the "this panel is loading"
 * case, which is most of them.
 *
 * Replaces the hand-rolled `<div className="relay-ui-internal flex items-center justify-center
 * h-64"><Spinner /></div>` that appears on nearly every screen, each with a
 * slightly different height.
 */
export const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  function Loading(
    { pad, fill, className, size = 'lg', title, description, label, ...props },
    ref
  ) {
    if (!title && !description) {
      return (
        <div
          ref={ref}
          className={cn(spinnerCenterRecipe({ pad, fill }), className)}
        >
          <Spinner size={size} tone="muted" label={label} {...props} />
        </div>
      );
    }
    /*
     * With a visible title the container is the status region and the
     * spinner stays quiet, so a screen reader hears the words once. `label`
     * still wins when given: it is the more specific announcement.
     */
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label ?? undefined}
        className={cn(
          spinnerCenterRecipe({ pad, fill, direction: 'column', gap: 3 }),
          'text-center',
          className
        )}
      >
        <Spinner size={size} tone="muted" label={null} {...props} />
        <div className="relay-ui-internal flex flex-col gap-1">
          {title ? <Text variant="body-strong">{title}</Text> : null}
          {description ? (
            <Text variant="body-sm" tone="muted">
              {description}
            </Text>
          ) : null}
        </div>
      </div>
    );
  }
);
